import { useCallback, useRef, useState } from "react";

type AnswerSave = {
  answer: string | null;
  run: () => Promise<void>;
};

export function useAnswerSaveTracker() {
  const pendingSavesRef = useRef(new Set<Promise<void>>());
  const latestSaveByQuestionIdRef = useRef(new Map<string, AnswerSave>());
  const failedQuestionIdsRef = useRef(new Set<string>());
  const [hasFailedSaves, setHasFailedSaves] = useState(false);

  const trackSave = useCallback(
    (questionId: string, answer: string | null, run: () => Promise<void>) => {
      const save: AnswerSave = { answer, run };
      latestSaveByQuestionIdRef.current.set(questionId, save);

      const pending = (async () => {
        let succeeded = true;
        try {
          await run();
        } catch {
          succeeded = false;
        }
        if (latestSaveByQuestionIdRef.current.get(questionId) !== save) {
          return;
        }
        if (succeeded) {
          failedQuestionIdsRef.current.delete(questionId);
        } else {
          failedQuestionIdsRef.current.add(questionId);
        }
        setHasFailedSaves(failedQuestionIdsRef.current.size > 0);
      })();

      pendingSavesRef.current.add(pending);
      void pending.finally(() => pendingSavesRef.current.delete(pending));
      return pending;
    },
    [],
  );

  const flushSaves = useCallback(async (): Promise<boolean> => {
    while (pendingSavesRef.current.size > 0) {
      await Promise.all([...pendingSavesRef.current]);
    }
    return failedQuestionIdsRef.current.size === 0;
  }, []);

  const retryFailedSaves = useCallback(async (): Promise<boolean> => {
    for (const questionId of [...failedQuestionIdsRef.current]) {
      const save = latestSaveByQuestionIdRef.current.get(questionId);
      if (save) {
        void trackSave(questionId, save.answer, save.run);
      }
    }
    return flushSaves();
  }, [flushSaves, trackSave]);

  return { hasFailedSaves, trackSave, flushSaves, retryFailedSaves };
}
